import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { ArrowLeft, Play, CheckCircle, DollarSign, Plus, Trash2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { tripService } from '../../services/trip.service';
import { tripFinanceService } from '../../services/tripFinance.service';
import { vehicleExpenseService } from '../../services/vehicleExpense.service';
import { TripStatus, UserRole, ExpenseCategory, ExpenseStatus } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { useAuthStore } from '../../store/auth.store';

export const TripDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasAnyRole } = useAuthStore();

  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isEditAmountModalOpen, setIsEditAmountModalOpen] = useState(false);
  const [isTripExpenseModalOpen, setIsTripExpenseModalOpen] = useState(false);
  const [startOdometer, setStartOdometer] = useState('');
  const [endOdometer, setEndOdometer] = useState('');
  const [editAmount, setEditAmount] = useState('');
  
  // Trip expense form states
  const [expenseCategory, setExpenseCategory] = useState<ExpenseCategory>(ExpenseCategory.DRIVER_ALLOWANCE);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [expenseVendor, setExpenseVendor] = useState('');
  
  const canManageFinance = hasAnyRole([UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.FINANCE_OFFICER]);

  const { data: trip, isLoading, error } = useQuery({
    queryKey: ['trip', id],
    queryFn: () => tripService.getById(Number(id)),
    enabled: !!id,
  });

  const { data: financialSummary } = useQuery({
    queryKey: ['tripFinancialSummary', id],
    queryFn: () => tripFinanceService.getFinancialSummary(Number(id)),
    enabled: !!id,
  });

  const startMutation = useMutation({
    mutationFn: (odometer: number) => tripService.start(Number(id), odometer),
    onSuccess: () => {
      toast.success('Trip started successfully');
      queryClient.invalidateQueries({ queryKey: ['trip', id] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      setIsStartModalOpen(false);
      setStartOdometer('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to start trip');
    },
  });

  const completeMutation = useMutation({
    mutationFn: (odometer: number) => tripService.complete(Number(id), odometer),
    onSuccess: () => {
      toast.success('Trip completed successfully');
      queryClient.invalidateQueries({ queryKey: ['trip', id] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      setIsCompleteModalOpen(false);
      setEndOdometer('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to complete trip');
    },
  });

  const updateAmountMutation = useMutation({
    mutationFn: (amount: number) => tripService.updateAmount(Number(id), amount),
    onSuccess: () => {
      toast.success('Trip amount updated successfully');
      queryClient.invalidateQueries({ queryKey: ['trip', id] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      setIsEditAmountModalOpen(false);
      setEditAmount('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to update amount');
    },
  });

  const handleStartTrip = () => {
    const odometer = parseFloat(startOdometer);
    if (isNaN(odometer) || odometer <= 0) {
      toast.error('Please enter a valid odometer reading');
      return;
    }
    startMutation.mutate(odometer);
  };

  const handleCompleteTrip = () => {
    const odometer = parseFloat(endOdometer);
    if (isNaN(odometer) || odometer <= 0) {
      toast.error('Please enter a valid odometer reading');
      return;
    }
    completeMutation.mutate(odometer);
  };

  const handleUpdateAmount = () => {
    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    updateAmountMutation.mutate(amount);
  };

  const handleOpenEditAmount = () => {
    setEditAmount(trip?.amount?.toString() || '');
    setIsEditAmountModalOpen(true);
  };

  // Trip Expense mutation (creates a VehicleExpense linked to this trip)
  const createTripExpenseMutation = useMutation({
    mutationFn: (data: { category: ExpenseCategory; amount: number; description?: string; expenseDate?: string; vendor?: string }) =>
      vehicleExpenseService.recordExpense({
        vehicleId: trip?.vehicleId || 0,
        tripId: Number(id),
        expenseDate: data.expenseDate || new Date().toISOString().split('T')[0],
        category: data.category,
        amount: data.amount,
        description: data.description,
        vendor: data.vendor,
      }),
    onSuccess: () => {
      toast.success('Expense recorded');
      queryClient.invalidateQueries({ queryKey: ['tripFinancialSummary', id] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      setIsTripExpenseModalOpen(false);
      setExpenseAmount('');
      setExpenseDescription('');
      setExpenseVendor('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to record expense');
    },
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: (expenseId: number) => vehicleExpenseService.delete(expenseId),
    onSuccess: () => {
      toast.success('Expense deleted');
      queryClient.invalidateQueries({ queryKey: ['tripFinancialSummary', id] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to delete expense');
    },
  });

  const handleAddTripExpense = () => {
    const amount = parseFloat(expenseAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    createTripExpenseMutation.mutate({
      category: expenseCategory,
      amount,
      description: expenseDescription || undefined,
      expenseDate: expenseDate || undefined,
      vendor: expenseVendor || undefined,
    });
  };

  // Expense category labels
  const expenseCategoryLabels: Record<ExpenseCategory, string> = {
    [ExpenseCategory.FUEL]: 'Fuel',
    [ExpenseCategory.MAINTENANCE]: 'Maintenance',
    [ExpenseCategory.REPAIR]: 'Repair',
    [ExpenseCategory.INSURANCE]: 'Insurance',
    [ExpenseCategory.LATRA]: 'LATRA',
    [ExpenseCategory.REGISTRATION]: 'Registration',
    [ExpenseCategory.PARKING]: 'Parking',
    [ExpenseCategory.TOLLS]: 'Tolls',
    [ExpenseCategory.CLEANING]: 'Cleaning',
    [ExpenseCategory.TIRES]: 'Tires',
    [ExpenseCategory.PARTS]: 'Parts',
    [ExpenseCategory.LABOR]: 'Labor',
    [ExpenseCategory.INSPECTION]: 'Inspection',
    [ExpenseCategory.FINES]: 'Fines',
    [ExpenseCategory.DRIVER_ALLOWANCE]: 'Driver Allowance',
    [ExpenseCategory.ACCOMMODATION]: 'Accommodation',
    [ExpenseCategory.MEALS]: 'Meals',
    [ExpenseCategory.TOLL_ROAD]: 'Toll/Road',
    [ExpenseCategory.LOADING_OFFLOADING]: 'Loading/Offloading',
    [ExpenseCategory.WEIGHBRIDGE]: 'Weighbridge',
    [ExpenseCategory.BORDER_FEES]: 'Border Fees',
    [ExpenseCategory.FUEL_ADVANCE]: 'Fuel Advance',
    [ExpenseCategory.OTHER]: 'Other',
  };

  // Categories that are considered allowances (money given to driver)
  const allowanceCategories = [
    ExpenseCategory.DRIVER_ALLOWANCE,
    ExpenseCategory.ACCOMMODATION,
    ExpenseCategory.MEALS,
    ExpenseCategory.FUEL_ADVANCE,
  ];

  // Categories that are considered trip expenses
  const tripExpenseCategories = [
    ExpenseCategory.FUEL,
    ExpenseCategory.TOLL_ROAD,
    ExpenseCategory.TOLLS,
    ExpenseCategory.PARKING,
    ExpenseCategory.REPAIR,
    ExpenseCategory.LOADING_OFFLOADING,
    ExpenseCategory.WEIGHBRIDGE,
    ExpenseCategory.BORDER_FEES,
    ExpenseCategory.OTHER,
  ];

  const getExpenseStatusVariant = (status: ExpenseStatus) => {
    switch (status) {
      case ExpenseStatus.APPROVED:
        return 'success';
      case ExpenseStatus.PENDING:
        return 'warning';
      case ExpenseStatus.REJECTED:
        return 'error';
      case ExpenseStatus.PAID:
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusBadgeVariant = (status: TripStatus) => {
    switch (status) {
      case TripStatus.PLANNED:
        return 'info';
      case TripStatus.ACTIVE:
        return 'success';
      case TripStatus.COMPLETED:
        return 'default';
      case TripStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading trip details...</p>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/trips')}>
          <ArrowLeft size={20} className="mr-2" />
          Back to Trips
        </Button>
        <div className="text-center py-8">
          <p className="text-error">Failed to load trip details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/trips')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-charcoal">{trip.tripNumber}</h1>
            <p className="text-gray-600">Trip Details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManageFinance && (trip.status === TripStatus.ACTIVE || trip.status === TripStatus.COMPLETED) && (
            <Button variant="secondary" onClick={() => setIsTripExpenseModalOpen(true)}>
              <DollarSign size={16} className="mr-2" />
              Add Expense
            </Button>
          )}
          {trip.status === TripStatus.PLANNED && (
            <Button variant="primary" onClick={() => setIsStartModalOpen(true)}>
              <Play size={16} className="mr-2" />
              Start Trip
            </Button>
          )}
          {trip.status === TripStatus.ACTIVE && (
            <Button variant="primary" onClick={() => setIsCompleteModalOpen(true)}>
              <CheckCircle size={16} className="mr-2" />
              Complete Trip
            </Button>
          )}
        </div>
      </div>

      {/* Trip Financial Summary */}
      <Card title="Trip Financial Summary">
        <div className="space-y-4">
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-600">Trip Price</span>
            <span className="font-medium text-lg text-green-600">
              {trip.fixedPrice ? (
                formatCurrency(trip.fixedPrice)
              ) : trip.amount ? (
                formatCurrency(trip.amount)
              ) : (
                <span className="text-gray-400">Not set</span>
              )}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-600">Total Trip Allowances</span>
            <span className="font-medium text-red-600">
              - {formatCurrency(financialSummary?.totalAllowances || 0)}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span className="text-gray-600">Total Trip Expenses</span>
            <span className="font-medium text-red-600">
              - {formatCurrency(financialSummary?.totalExpenses || 0)}
            </span>
          </div>
          <div className="flex justify-between py-3 border-t-2 border-gray-300 mt-2">
            <span className="text-gray-900 font-semibold text-lg">Remaining Balance</span>
            <span className={`font-bold text-xl ${financialSummary?.isOverBudget ? 'text-red-600' : 'text-primary'}`}>
              {(trip.fixedPrice || trip.amount) ? formatCurrency(financialSummary?.remainingBalance || 0) : <span className="text-gray-400">-</span>}
            </span>
          </div>
          {financialSummary?.isOverBudget && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-600" />
              <p className="text-sm text-red-800">This trip has exceeded its available balance.</p>
            </div>
          )}
          {!trip.fixedPrice && !trip.amount && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">Set the trip price to calculate remaining balance.</p>
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Trip Information">
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Status</span>
              <Badge variant={getStatusBadgeVariant(trip.status)}>{trip.status}</Badge>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Purpose</span>
              <span className="font-medium text-right max-w-xs">{trip.purpose}</span>
            </div>
            {trip.jobReference && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Job Reference</span>
                <span className="font-medium">{trip.jobReference}</span>
              </div>
            )}
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Start Date</span>
              <span className="font-medium">{format(parseISO(trip.startDate), 'MMM dd, yyyy')}</span>
            </div>
            {trip.endDate && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">End Date</span>
                <span className="font-medium">{format(parseISO(trip.endDate), 'MMM dd, yyyy')}</span>
              </div>
            )}
          </div>
        </Card>

        <Card title="Route Details">
          <div className="space-y-4">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Origin</span>
              <span className="font-medium">{trip.origin}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Destination</span>
              <span className="font-medium">{trip.destination}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Planned Distance</span>
              <span className="font-medium">{trip.plannedDistanceKm} km</span>
            </div>
            {trip.actualDistanceKm && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Actual Distance</span>
                <span className="font-medium">{trip.actualDistanceKm} km</span>
              </div>
            )}
            {trip.weightInTons && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Weight</span>
                <span className="font-medium">{trip.weightInTons} tons</span>
              </div>
            )}
          </div>
        </Card>

        <Card title="Vehicle & Driver">
          <div className="space-y-4">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Vehicle</span>
              <span className="font-medium">{trip.vehicleRegistration}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Driver</span>
              <span className="font-medium">{trip.driverName}</span>
            </div>
            {trip.startOdometerReading && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Start Odometer</span>
                <span className="font-medium">{trip.startOdometerReading} km</span>
              </div>
            )}
            {trip.endOdometerReading && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">End Odometer</span>
                <span className="font-medium">{trip.endOdometerReading} km</span>
              </div>
            )}
          </div>
        </Card>

        <Card title="Pricing Information">
          <div className="space-y-4">
            {trip.ratePerKm && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Rate per KM</span>
                <span className="font-medium">{formatCurrency(trip.ratePerKm)}</span>
              </div>
            )}
            {trip.ratePerTon && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Rate per Ton</span>
                <span className="font-medium">{formatCurrency(trip.ratePerTon)}</span>
              </div>
            )}
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600">Total Amount</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-lg">
                  {trip.amount ? formatCurrency(trip.amount) : 'Not set'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenEditAmount}
                  className="text-xs"
                >
                  Edit
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <Card title="Timestamps">
          <div className="space-y-4">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Created</span>
              <span className="font-medium">{format(parseISO(trip.createdAt), 'MMM dd, yyyy HH:mm')}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Last Updated</span>
              <span className="font-medium">{format(parseISO(trip.updatedAt), 'MMM dd, yyyy HH:mm')}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Trip Allowances Section (from expenses with allowance categories) */}
      <Card 
        title="Trip Allowances"
        action={
          canManageFinance && (
            <Button size="sm" variant="secondary" onClick={() => {
              setExpenseCategory(ExpenseCategory.DRIVER_ALLOWANCE);
              setIsTripExpenseModalOpen(true);
            }}>
              <Plus size={14} className="mr-1" />
              Add Allowance
            </Button>
          )
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Expense #</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Date</TableHead>
              {canManageFinance && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(!financialSummary?.allowances || financialSummary.allowances.length === 0) ? (
              <TableRow>
                <td colSpan={canManageFinance ? 7 : 6} className="px-6 py-4 text-center text-sm text-gray-500">
                  No trip allowances recorded.
                </td>
              </TableRow>
            ) : (
              financialSummary.allowances.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell className="font-medium">{expense.expenseNumber}</TableCell>
                  <TableCell>{expenseCategoryLabels[expense.category]}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(expense.amount)}</TableCell>
                  <TableCell>
                    <Badge variant={getExpenseStatusVariant(expense.status)}>{expense.status}</Badge>
                  </TableCell>
                  <TableCell>{expense.description || '-'}</TableCell>
                  <TableCell>{format(parseISO(expense.expenseDate), 'MMM dd, yyyy')}</TableCell>
                  {canManageFinance && expense.status === ExpenseStatus.PENDING && (
                    <TableCell>
                      <button
                        onClick={() => deleteExpenseMutation.mutate(expense.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  )}
                  {canManageFinance && expense.status !== ExpenseStatus.PENDING && (
                    <TableCell>-</TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Trip Expenses Section (from expenses with expense categories) */}
      <Card 
        title="Trip Expenses"
        action={
          canManageFinance && (
            <Button size="sm" variant="secondary" onClick={() => {
              setExpenseCategory(ExpenseCategory.FUEL);
              setIsTripExpenseModalOpen(true);
            }}>
              <Plus size={14} className="mr-1" />
              Add Expense
            </Button>
          )
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Expense #</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Description</TableHead>
              {canManageFinance && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(!financialSummary?.expenses || financialSummary.expenses.length === 0) ? (
              <TableRow>
                <td colSpan={canManageFinance ? 8 : 7} className="px-6 py-4 text-center text-sm text-gray-500">
                  No trip expenses recorded.
                </td>
              </TableRow>
            ) : (
              financialSummary.expenses.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell className="font-medium">{expense.expenseNumber}</TableCell>
                  <TableCell>{expenseCategoryLabels[expense.category]}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(expense.amount)}</TableCell>
                  <TableCell>
                    <Badge variant={getExpenseStatusVariant(expense.status)}>{expense.status}</Badge>
                  </TableCell>
                  <TableCell>{format(parseISO(expense.expenseDate), 'MMM dd, yyyy')}</TableCell>
                  <TableCell>{expense.vendor || '-'}</TableCell>
                  <TableCell>{expense.description || '-'}</TableCell>
                  {canManageFinance && expense.status === ExpenseStatus.PENDING && (
                    <TableCell>
                      <button
                        onClick={() => deleteExpenseMutation.mutate(expense.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  )}
                  {canManageFinance && expense.status !== ExpenseStatus.PENDING && (
                    <TableCell>-</TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Start Trip Modal */}
      <Modal
        isOpen={isStartModalOpen}
        onClose={() => setIsStartModalOpen(false)}
        title="Start Trip"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsStartModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleStartTrip} loading={startMutation.isPending}>
              Start Trip
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600">Enter the current odometer reading to start this trip.</p>
          <Input
            label="Start Odometer Reading (km)"
            type="number"
            value={startOdometer}
            onChange={(e) => setStartOdometer(e.target.value)}
            placeholder="e.g., 45000"
            required
          />
        </div>
      </Modal>

      {/* Complete Trip Modal */}
      <Modal
        isOpen={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        title="Complete Trip"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsCompleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCompleteTrip} loading={completeMutation.isPending}>
              Complete Trip
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600">Enter the final odometer reading to complete this trip.</p>
          <Input
            label="End Odometer Reading (km)"
            type="number"
            value={endOdometer}
            onChange={(e) => setEndOdometer(e.target.value)}
            placeholder="e.g., 45500"
            required
          />
        </div>
      </Modal>

      {/* Edit Amount Modal */}
      <Modal
        isOpen={isEditAmountModalOpen}
        onClose={() => setIsEditAmountModalOpen(false)}
        title="Edit Trip Amount"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsEditAmountModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateAmount} loading={updateAmountMutation.isPending}>
              Update Amount
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600">Update the total amount for this trip.</p>
          <Input
            label="Total Amount (TZS)"
            type="number"
            step="0.01"
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            placeholder="e.g., 243000"
            required
          />
          {trip.ratePerKm && trip.plannedDistanceKm && !trip.ratePerTon && (
            <div className="text-sm text-gray-600">
              <strong>Suggested:</strong> {Number(trip.plannedDistanceKm)} km × {formatCurrency(trip.ratePerKm)} = {formatCurrency(trip.ratePerKm * trip.plannedDistanceKm)}
            </div>
          )}
          {trip.ratePerTon && trip.weightInTons && !trip.ratePerKm && (
            <div className="text-sm text-gray-600">
              <strong>Suggested:</strong> {Number(trip.weightInTons)} tons × {formatCurrency(trip.ratePerTon)} = {formatCurrency(trip.ratePerTon * trip.weightInTons)}
            </div>
          )}
        </div>
      </Modal>

      {/* Add Trip Expense/Allowance Modal */}
      <Modal
        isOpen={isTripExpenseModalOpen}
        onClose={() => setIsTripExpenseModalOpen(false)}
        title={allowanceCategories.includes(expenseCategory) ? "Add Trip Allowance" : "Add Trip Expense"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsTripExpenseModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddTripExpense} loading={createTripExpenseMutation.isPending}>
              {allowanceCategories.includes(expenseCategory) ? "Add Allowance" : "Add Expense"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={expenseCategory}
              onChange={(e) => setExpenseCategory(e.target.value as ExpenseCategory)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <optgroup label="Allowances">
                {allowanceCategories.map((cat) => (
                  <option key={cat} value={cat}>{expenseCategoryLabels[cat]}</option>
                ))}
              </optgroup>
              <optgroup label="Expenses">
                {tripExpenseCategories.map((cat) => (
                  <option key={cat} value={cat}>{expenseCategoryLabels[cat]}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <Input
            label="Amount (TZS)"
            type="number"
            value={expenseAmount}
            onChange={(e) => setExpenseAmount(e.target.value)}
            placeholder="e.g., 50000"
            required
          />
          <Input
            label="Date"
            type="date"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
          />
          <Input
            label="Vendor (Optional)"
            value={expenseVendor}
            onChange={(e) => setExpenseVendor(e.target.value)}
            placeholder="e.g., Shell Station"
          />
          <Input
            label="Description (Optional)"
            value={expenseDescription}
            onChange={(e) => setExpenseDescription(e.target.value)}
            placeholder="e.g., Driver daily allowance"
          />
          <p className="text-xs text-gray-500">
            This expense will be recorded and visible in both the Trip Details and the main Expenses page.
          </p>
        </div>
      </Modal>
    </div>
  );
};
